<template>
    <div>
        <!-- Hero Area （リストページ用のダミーHero） -->
        <div id="home" style="display: none;"></div>
        <div id="about" style="display: none;"></div>
        <div id="services" style="display: none;"></div>
        <div id="project" style="display: none;"></div>
        <div id="blog" style="display: none;"></div>
        
        <Navbar/>
        
        <!-- Breadcrumb Area -->
        <div class="breadcroumb-area gray-bg-2">
            <div class="container">
                <div class="row">
                    <div class="col-lg-12">
                        <div class="breadcroumb-title">
                            <h1 class="text-dark">ニュース一覧</h1>
                            <h6><router-link to="/">ホーム</router-link> / ニュース</h6>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- News List Section -->
        <div class="news-list-section section-padding">
            <div class="container">
                <!-- ローディング表示 -->
                <div v-if="loading" class="row">
                    <div class="col-12 text-center">
                        <p>ニュースを読み込んでいます...</p>
                    </div>
                </div>
                
                <!-- ニュース一覧 -->
                <div v-else>
                    <div class="row">
                        <div 
                            v-for="news in paginatedNews" 
                            :key="news.id"
                            class="col-lg-4 col-md-6 mb-4"
                        >
                            <div class="news-card">
                                <router-link :to="`/news/${news.id}`">
                                    <div class="news-image">
                                        <img 
                                            :src="getMainImage(news)" 
                                            :alt="news.title"
                                            @error="handleImageError"
                                        >
                                    </div>
                                    <div class="news-content">
                                        <div class="news-meta">
                                            <span class="news-date">{{ formatDate(news.date) }}</span>
                                        </div>
                                        <h3 class="news-title">{{ news.title }}</h3>
                                    </div>
                                </router-link>
                            </div>
                        </div>
                    </div>
                    
                    <!-- ページネーション -->
                    <div v-if="totalPages > 1" class="row mt-4">
                        <div class="col-12">
                            <nav aria-label="ニュースページネーション">
                                <ul class="pagination justify-content-center">
                                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                        <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">前へ</a>
                                    </li>
                                    <li 
                                        v-for="page in displayPages" 
                                        :key="page"
                                        class="page-item" 
                                        :class="{ active: currentPage === page }"
                                    >
                                        <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                                    </li>
                                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                        <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">次へ</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                    
                    <!-- ニュースがない場合 -->
                    <div v-if="allNews.length === 0" class="row">
                        <div class="col-12 text-center">
                            <p>現在、ニュースはありません。</p>
                            <router-link to="/" class="main-btn mt-3">ホームに戻る</router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <Footer/>
    </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'

export default {
    name: 'NewsList',
    components: {
        Navbar, Footer
    },
    data() {
        return {
            allNews: [],
            loading: true,
            currentPage: 1,
            itemsPerPage: 20,
            SHEET_ID: process.env.VUE_APP_GOOGLE_SHEET_ID || ''
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.allNews.length / this.itemsPerPage);
        },
        paginatedNews() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.allNews.slice(start, end);
        },
        displayPages() {
            const pages = [];
            const maxDisplay = 5;
            let start = Math.max(1, this.currentPage - 2);
            let end = Math.min(this.totalPages, start + maxDisplay - 1);
            
            if (end - start < maxDisplay - 1) {
                start = Math.max(1, end - maxDisplay + 1);
            }
            
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            return pages;
        }
    },
    async mounted() {
        window.scrollTo(0, 0);
        await this.fetchAllNews();
    },
    methods: {
        async fetchAllNews() {
            try {
                this.loading = true;
                
                if (!this.SHEET_ID) {
                    console.log('Google Sheet IDが設定されていません');
                    await this.loadFromJSON();
                    return;
                }
                
                // キャッシュ回避のためタイムスタンプを追加
                const CSV_URL = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/export?format=csv&gid=0&t=${Date.now()}`;
                
                try {
                    const response = await fetch(CSV_URL);
                    const csvText = await response.text();
                    this.allNews = this.parseCSV(csvText);
                } catch (corsError) {
                    await this.fetchWithProxy();
                }
                
            } catch (error) {
                console.error('ニュース取得エラー:', error);
                await this.loadFromJSON();
            } finally {
                this.loading = false;
            }
        },
        
        async fetchWithProxy() {
            try {
                // キャッシュ回避のためタイムスタンプを追加
                const CSV_URL = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/export?format=csv&gid=0&t=${Date.now()}`;
                const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(CSV_URL)}`;
                
                const response = await fetch(proxyUrl);
                const data = await response.json();
                this.allNews = this.parseCSV(data.contents);
            } catch (error) {
                console.error('プロキシ経由の取得もエラー:', error);
            }
        },
        
        async loadFromJSON() {
            try {
                const response = await fetch('/assets/data/blogs.json');
                const data = await response.json();
                this.allNews = data.blogs;
            } catch (error) {
                console.error('JSONからの読み込みエラー:', error);
            }
        },
        
        // CSV解析（改行を含むフィールドに対応）
        parseCSV(csvText) {
            const rows = this.parseCSVToRows(csvText);
            
            if (rows.length < 2) return [];
            
            const headers = rows[0].map(h => h.toLowerCase().trim());
            const newsItems = [];
            
            for (let i = 1; i < rows.length; i++) {
                const values = rows[i];
                const item = {};
                
                headers.forEach((header, index) => {
                    item[header] = values[index] || '';
                });
                
                // idとtitleが存在するか確認
                if (!item.id || !item.title) {
                    continue;
                }
                
                // published判定を改善（大文字・小文字・日本語対応）
                const publishedValue = (item.published || '').toString().toUpperCase().trim();
                if (publishedValue === 'TRUE' || publishedValue === '公開' || publishedValue === 'YES' || publishedValue === '1') {
                    newsItems.push(item);
                }
            }
            
            // 日付順にソート（新しい順）
            return newsItems.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        
        // CSVテキストを行の配列に変換（改行を含むフィールドに対応）
        parseCSVToRows(csvText) {
            const rows = [];
            let currentRow = [];
            let currentField = '';
            let inQuotes = false;
            
            for (let i = 0; i < csvText.length; i++) {
                const char = csvText[i];
                const nextChar = csvText[i + 1];
                
                if (inQuotes) {
                    if (char === '"') {
                        if (nextChar === '"') {
                            // エスケープされたダブルクォート
                            currentField += '"';
                            i++; // 次の文字をスキップ
                        } else {
                            // クォート終了
                            inQuotes = false;
                        }
                    } else {
                        // クォート内の文字（改行含む）
                        currentField += char;
                    }
                } else {
                    if (char === '"') {
                        // クォート開始
                        inQuotes = true;
                    } else if (char === ',') {
                        // フィールド区切り
                        currentRow.push(currentField.trim());
                        currentField = '';
                    } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
                        // 行終了
                        currentRow.push(currentField.trim());
                        if (currentRow.some(field => field !== '')) {
                            rows.push(currentRow);
                        }
                        currentRow = [];
                        currentField = '';
                        if (char === '\r') {
                            i++; // \nをスキップ
                        }
                    } else if (char === '\r') {
                        // 単独の\r（古いMac形式）
                        currentRow.push(currentField.trim());
                        if (currentRow.some(field => field !== '')) {
                            rows.push(currentRow);
                        }
                        currentRow = [];
                        currentField = '';
                    } else {
                        currentField += char;
                    }
                }
            }
            
            // 最後のフィールドと行を追加
            if (currentField || currentRow.length > 0) {
                currentRow.push(currentField.trim());
                if (currentRow.some(field => field !== '')) {
                    rows.push(currentRow);
                }
            }
            
            return rows;
        },
        
        getMainImage(news) {
            if (news.image1 && news.image1.trim()) {
                return news.image1;
            }
            if (news.imageurl && news.imageurl.trim()) {
                return news.imageurl;
            }
            if (news.image && news.image.trim()) {
                return news.image;
            }
            return 'https://www.gharb.tokyo/assets/img/blog/news1.png';
        },
        
        handleImageError(event) {
            event.target.src = 'https://www.gharb.tokyo/assets/img/blog/news1.png';
        },
        
        formatDate(date) {
            if (!date) return '';
            try {
                return new Date(date).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } catch (e) {
                return date;
            }
        },
        
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }
}
</script>

<style scoped>
.news-list-section {
    min-height: 50vh;
}

.news-card {
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    transition: transform 0.3s, box-shadow 0.3s;
}

.news-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 20px rgba(0,0,0,0.15);
}

.news-card a {
    text-decoration: none;
    color: inherit;
}

.news-image {
    width: 100%;
    height: 200px;
    overflow: hidden;
}

.news-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
}

.news-card:hover .news-image img {
    transform: scale(1.05);
}

.news-content {
    padding: 20px;
}

.news-meta {
    margin-bottom: 10px;
}

.news-date {
    font-size: 14px;
    color: #888;
}

.news-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
    line-height: 1.4;
}

.pagination .page-link {
    color: #7c5739;
    border-color: #7c5739;
}

.pagination .page-item.active .page-link {
    background-color: #7c5739;
    border-color: #7c5739;
    color: #fff;
}

.pagination .page-link:hover {
    background-color: #7c5739;
    border-color: #7c5739;
    color: #fff;
}

.pagination .page-item.disabled .page-link {
    color: #ccc;
    border-color: #ccc;
}
</style>